<template>
    <v-container fluid>
        <v-data-table
            :headers="headers"
            :items="reasonsFiltered"
            class="elevation-0"
            :mobile-breakpoint="0"
            :loading="loading"
            disable-pagination
            hide-default-footer
        >
            <template v-slot:top>
                <v-row
                    no-gutter
                    class="mt-0 mx-0 pt-4 pb-0 pb-4"
                    :style="{ 'background-color': '#eeeeee' }"
                >
                    <v-col cols="12" class="d-flex mb-n3">
                        <h1 class="mr-4">Rejection Reasons</h1>
                        <v-btn
                            rounded
                            color="primary"
                            @click="openCreateReason"
                            class="mx-2"
                        >
                            <v-icon>
                                mdi-plus
                            </v-icon>
                            {{ $vuetify.breakpoint.smAndDown ? '' : 'NEW' }}
                        </v-btn>
                    </v-col>
                </v-row>
            </template>
            <!--HEADERS-->
            <template v-slot:[`header.reason`]="{ header }">
                <v-text-field
                    class="pb-2 pt-3 mt-2"
                    :label="header.text"
                    v-model="searchByReason"
                    dense
                />
            </template>
            <!--ITEMS-->
            <template v-slot:[`item.actions`]="{ item }">
                <div class="d-flex justify-center">
                    <v-btn small icon @click="openEditReason(item)">
                        <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                    <v-btn small icon @click="openDeleteReason(item)">
                        <v-icon>mdi-delete</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-data-table>
        <!--REASON FORM-->
        <v-dialog
            v-model="openReasonForm"
            :retain-focus="false"
            persistent
            width="600px"
        >
            <RejectionReasonsForm
                v-if="openReasonForm"
                :createForm="createForm"
                :rejectionReason="selectedReason"
                :originalRejectionReason="originalReason"
                :reasonSettings="setting"
                @addReason="addReason"
                @replaceReason="replaceReason"
                @close="closeEditReason"
            />
        </v-dialog>
        <!--Delete Reason-->
        <v-dialog
            v-model="openDeleteWarning"
            persistent
            max-width="400px"
            :retain-focus="false"
        >
            <v-card :loading="loading" v-if="openDeleteWarning">
                <v-card-title class="text-h5"
                    >Delete Rejection Reason</v-card-title
                >
                <v-card-text>
                    Are you sure you want to delete this rejection reason?
                </v-card-text>
                <v-card-actions>
                    <v-btn text color="secondary" @click="closeDeleteReason">
                        Close
                    </v-btn>
                    <v-spacer></v-spacer>
                    <v-btn
                        @click="deleteReason"
                        text
                        color="error"
                        :loading="loading"
                    >
                        Delete
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-container>
</template>

<script>
import { mapMutations } from 'vuex'
import API from '@/services/api'
import _ from 'lodash'

export default {
    name: 'RejectionReasons',
    props: {
        setting: {
            type: Object,
            default: () => ({}),
        },
    },
    components: {
        RejectionReasonsForm: () =>
            import('@/components/Quality/RejectionReasonsForm.vue'),
    },
    data: () => ({
        loading: false,
        reasons: [],
        selectedReason: {},
        originalReason: {},
        createForm: true,
        openReasonForm: false,
        openDeleteWarning: false,
        reasonSettings: {},
        headers: [
            {
                text: 'NAME',
                value: 'reason',
                sortable: false,
                width: '200',
            },
            {
                text: 'DESCRIPTION',
                value: 'description',
                sortable: false,
            },
            {
                text: 'ACTIONS',
                value: 'actions',
                sortable: false,
                align: 'center',
            },
        ],
        searchByReason: undefined,
        settingId: undefined,
    }),
    async mounted() {
        try {
            this.loading = true
            this.settingsId = this.setting.quality?.id || this.setting.id
            this.reasons = await API.getRejectionReasons(this.settingsId)
        } catch (error) {
            this.setErrorItems({
                name: this.$options.name,
                message: error.message,
            })
        } finally {
            this.loading = false
        }
    },
    computed: {
        reasonsFiltered() {
            if (this.searchByReason) {
                return this.reasons.filter(reason =>
                    reason.reason
                        .toLowerCase()
                        .includes(this.searchByReason.toLowerCase())
                )
            }
            return this.reasons
        },
    },
    methods: {
        ...mapMutations(['setErrorItems']),
        replaceReason(reasonToUpdate) {
            const index = this.reasons.findIndex(
                reason => reason.id == reasonToUpdate.id
            )
            if (index >= 0) {
                this.reasons.splice(index, 1, {
                    ...this.reasons[index],
                    ...reasonToUpdate,
                })
            }
        },
        addReason(reason) {
            this.reasons.push(reason)
            this.searchByReason = ''
        },
        removeReason() {
            const index = this.reasons.findIndex(
                reason => reason.id == this.selectedReason.id
            )
            if (index >= 0) {
                this.reasons.splice(index, 1)
            }
        },
        async deleteReason() {
            try {
                this.loading = true
                await API.deleteRejectionReason({
                    settingId: this.settingsId,
                    id: this.selectedReason.id,
                })
                this.removeReason()
                this.closeDeleteReason()
            } catch (error) {
                this.setErrorItems({
                    source: this.$options.name,
                    message: error.message,
                })
            } finally {
                this.loading = false
            }
        },
        closeDeleteReason() {
            this.selectedReason = {}
            this.openDeleteWarning = false
        },
        openDeleteReason(item) {
            this.selectedReason = _.cloneDeep(item)
            this.openDeleteWarning = true
        },
        closeEditReason() {
            this.createForm = true
            this.selectedReason = {}
            this.originalReason = {}
            this.openReasonForm = false
        },
        openEditReason(item) {
            this.createForm = false
            this.selectedReason = _.cloneDeep(item)
            this.originalReason = _.cloneDeep(item)
            this.openReasonForm = true
        },
        openCreateReason() {
            this.createForm = true
            this.openReasonForm = true
        },
    },
}
</script>

<style>
.v-data-table {
    border: 1px solid #eeeeee;
}
thead {
    background: #eeeeee;
}
.v-input .v-label {
    font-size: 12px;
}
</style>
